import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, MapPin, Navigation } from 'lucide-react';
import Map, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import toast from 'react-hot-toast';
import axios from 'axios';
import trackingService from '../services/trackingService';
import PathVisualizer from './PathVisualizer';
import TransportModeSelector from './TransportModeSelector';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewState, setViewState] = useState({
    longitude: 4.9041,
    latitude: 52.3676,
    zoom: 10
  });
  const [hasUserLocation, setHasUserLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [currentPath, setCurrentPath] = useState([]);
  const [matchedPath, setMatchedPath] = useState([]);
  const [mapMatchingStats, setMapMatchingStats] = useState(null);
  const [completedBlocks, setCompletedBlocks] = useState([]);
  const [transportMode, setTransportMode] = useState('walking');
  const [showModeColors, setShowModeColors] = useState(false);
  const [showMatchedPath, setShowMatchedPath] = useState(true);
  const [highlightedLandmark, setHighlightedLandmark] = useState(null);

  // Handle landmark highlighting from suggestions page
  useEffect(() => {
    if (location.state?.highlightLandmark) {
      const landmark = location.state.highlightLandmark;
      setHighlightedLandmark(landmark);
      setViewState({
        longitude: landmark.longitude,
        latitude: landmark.latitude,
        zoom: 15
      });
      
      // Clear the state after handling it
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Check if we should restore location when component mounts (run first)
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    console.log('Checking saved location:', savedLocation);
    if (savedLocation) {
      try {
        const { longitude, latitude } = JSON.parse(savedLocation);
        console.log('Restoring saved location - longitude:', longitude, 'latitude:', latitude);
        setUserLocation({ longitude, latitude });
        setHasUserLocation(true);
        setViewState({
          longitude,
          latitude,
          zoom: 12
        });
      } catch (error) {
        console.error('Error parsing saved location:', error);
      }
    }
  }, []);

  // Check location permission and load existing data on mount
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setLocationPermission(result.state);
        // Only get current location if we don't have a saved location
        const savedLocation = localStorage.getItem('userLocation');
        if (result.state === 'granted' && !savedLocation) {
          getCurrentLocation();
        }
      });
    }

    // Load existing tracking data
    loadExistingTrackingData();
  }, []);

  const loadExistingTrackingData = async () => {
    try {
      // Load completed blocks
      const blocksResponse = await axios.get('/api/gps/completed-blocks');
      const blocks = blocksResponse.data.blocks.map(block => ({
        path: block.geojson.coordinates[0].map(coord => ({
          longitude: coord[0],
          latitude: coord[1]
        })),
        shouldMarkArea: true, // All saved blocks are marked areas
        totalDistance: block.totalDistance,
        area: block.areaKm2
      }));
      setCompletedBlocks(blocks);

      // Load tracking stats (for future use)
      const statsResponse = await axios.get('/api/gps/stats');
      console.log('Tracking stats received:', JSON.stringify(statsResponse.data, null, 2));
    } catch (error) {
      console.error('Error loading tracking data:', error);
    }
  };

  const getCurrentLocation = () => {
    console.log('getCurrentLocation called');
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          const locationData = { longitude, latitude };
          console.log('Location obtained:', locationData);
          
          setUserLocation(locationData);
          setHasUserLocation(true);
          setViewState({
            longitude,
            latitude,
            zoom: 15 // Increased zoom for better detail
          });
          
          // Save to localStorage for persistence
          localStorage.setItem('userLocation', JSON.stringify(locationData));
          console.log('Location saved to localStorage');
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Could not get your location');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    }
  };

  const requestLocationPermission = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationPermission('granted');
          getCurrentLocation();
        },
        (error) => {
          setLocationPermission('denied');
          toast.error('Location permission denied');
        }
      );
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const toggleTracking = () => {
    if (!tracking) {
      // Start tracking
      trackingService.startTracking(
        (newLocation) => {
          setUserLocation(newLocation);
        },
        (path, blockInfo) => {
          setCurrentPath(path);
          if (blockInfo && blockInfo.isCompleteBlock) {
            setCompletedBlocks(prev => [...prev, blockInfo]);
            if (blockInfo.shouldMarkArea) {
              toast.success(`Block completed! Area marked (${blockInfo.totalDistance.toFixed(1)}km)`);
            } else {
              toast.info(`Block completed but not marked - ${blockInfo.unwalkedRoadsCount} internal roads unwalked`);
            }
          }
        },
        transportMode
      );
      setTracking(true);
      toast.success('Tracking started');
    } else {
      // Stop tracking
      const finalPath = trackingService.stopTracking();
      setTracking(false);
      
      if (finalPath && finalPath.length > 0) {
        // Perform map matching and save the session
        performMapMatchingAndSave();
      }
      
      toast.success('Tracking stopped');
    }
  };

  // Perform map matching and save session
  const performMapMatchingAndSave = async () => {
    try {
      toast.loading('Performing map matching...', { id: 'map-matching' });
      
      // Perform HMM map matching
      const result = await trackingService.performMapMatching();
      
      if (result) {
        setMatchedPath(result.matchedPath);
        setMapMatchingStats(result.stats);
        
        toast.success(`Map matching complete: ${result.stats.matchRate.toFixed(1)}% match rate`, { id: 'map-matching' });
        
        // Save the session with map matching data
        await trackingService.saveTrackingSession();
        toast.success('Tracking session saved with map matching!');
      } else {
        toast.error('Map matching failed', { id: 'map-matching' });
      }
    } catch (error) {
      console.error('Map matching error:', error);
      toast.error('Map matching failed', { id: 'map-matching' });
    }
  };

  // Manual map matching for current path
  const performManualMapMatching = async () => {
    if (currentPath.length === 0) {
      toast.error('No GPS path available for map matching');
      return;
    }

    try {
      toast.loading('Performing map matching...', { id: 'manual-map-matching' });
      
      const result = await trackingService.performMapMatching();
      
      if (result) {
        setMatchedPath(result.matchedPath);
        setMapMatchingStats(result.stats);
        
        toast.success(`Map matching complete: ${result.stats.matchRate.toFixed(1)}% match rate`, { id: 'manual-map-matching' });
      } else {
        toast.error('Map matching failed', { id: 'manual-map-matching' });
      }
    } catch (error) {
      console.error('Manual map matching error:', error);
      toast.error('Map matching failed', { id: 'manual-map-matching' });
    }
  };

  const menuItems = [
    { name: 'Suggestions', icon: '📍', action: () => navigate('/suggestions') },
    { name: 'Statistics', icon: '📊', action: () => navigate('/statistics') },
    { name: 'Settings', icon: '⚙️', action: () => navigate('/settings') },
    { name: 'Log Out', icon: '🚪', action: handleLogout }
  ];

  return (
    <div className="h-screen w-full relative bg-gray-100">
      {/* Map Container */}
      <div className="absolute inset-0">
        <Map
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        >

          {/* User Location Marker */}
          {userLocation && (
            <Marker
              longitude={userLocation.longitude}
              latitude={userLocation.latitude}
              anchor="bottom"
            >
              <div className="w-6 h-6 bg-blue-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </Marker>
          )}

          {/* Highlighted Landmark Marker */}
          {highlightedLandmark && (
            <Marker
              longitude={highlightedLandmark.longitude}
              latitude={highlightedLandmark.latitude}
              anchor="bottom"
            >
              <div className="w-8 h-8 bg-red-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
            </Marker>
          )}

          {/* Path Visualization */}
          <PathVisualizer
            currentPath={currentPath}
            matchedPath={matchedPath}
            mapMatchingStats={mapMatchingStats}
            completedBlocks={completedBlocks}
            tracking={tracking}
            transportMode={transportMode}
            showModeColors={showModeColors}
            showMatchedPath={showMatchedPath}
          />
        </Map>
      </div>

      {/* Transport Mode Selector */}
      <TransportModeSelector
        currentMode={transportMode}
        onModeChange={setTransportMode}
      />

      {/* Map Matching Controls */}
      {(currentPath.length > 0 || matchedPath.length > 0) && (
        <div className="absolute bottom-4 left-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 p-3 min-w-[200px]">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-900">Map Matching</h3>
            <button
              onClick={() => setShowMatchedPath(!showMatchedPath)}
              className={`px-2 py-1 text-xs rounded ${
                showMatchedPath 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {showMatchedPath ? 'ON' : 'OFF'}
            </button>
          </div>
          
          {mapMatchingStats && (
            <div className="text-xs text-gray-600 space-y-1">
              <div>Match Rate: <span className="font-medium">{mapMatchingStats.matchRate.toFixed(1)}%</span></div>
              <div>Matched: <span className="font-medium">{mapMatchingStats.matchedPoints}/{mapMatchingStats.totalPoints}</span></div>
              {mapMatchingStats.averageConfidence && (
                <div>Confidence: <span className="font-medium">{(mapMatchingStats.averageConfidence * 100).toFixed(1)}%</span></div>
              )}
            </div>
          )}
          
          {currentPath.length > 0 && matchedPath.length === 0 && (
            <button
              onClick={performManualMapMatching}
              className="w-full mt-2 px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Match to Roads
            </button>
          )}
          
          <div className="mt-2 text-xs text-gray-500">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-0.5 bg-green-500"></div>
              <span>Matched roads</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-0.5 bg-red-500"></div>
              <span>Unmatched GPS</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-0.5 bg-gray-400 border-dashed border-t border-gray-400"></div>
              <span>Original GPS</span>
            </div>
          </div>
        </div>
      )}

      {/* Floating Hamburger Menu Button */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-3 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200"
        >
          {menuOpen ? (
            <X className="w-6 h-6 text-gray-700" />
          ) : (
            <Menu className="w-6 h-6 text-gray-700" />
          )}
        </button>
      </div>

      {/* Hamburger Menu */}
      {menuOpen && (
        <div className="absolute top-16 right-4 z-20 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[200px]">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {user?.first_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email
                  }
                </p>
                <p className="text-xs text-gray-500">MapMarker</p>
              </div>
            </div>
          </div>
          
          {/* Menu Items */}
          <div className="py-2">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  item.action();
                  setMenuOpen(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors"
              >
                <span className="text-lg">{item.icon}</span>
                <span className="text-gray-700">{item.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Location Permission Banner */}
      {locationPermission === 'denied' && (
        <div className="absolute bottom-20 left-4 right-4 z-10 bg-red-500 text-white p-4 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5" />
              <span className="text-sm">Location access needed for tracking</span>
            </div>
            <button
              onClick={requestLocationPermission}
              className="text-sm underline hover:no-underline"
            >
              Enable
            </button>
          </div>
        </div>
      )}

      {/* Tracking Controls */}
      <div className="absolute bottom-4 left-4 right-4 z-10 mobile-controls">
        <div className="flex space-x-3">
          {/* Location Button */}
          <button
            onClick={getCurrentLocation}
            className="flex-1 bg-white rounded-lg shadow-lg border border-gray-200 p-3 flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors"
          >
            <MapPin className="w-5 h-5 text-gray-700" />
            <span className="text-sm font-medium text-gray-700">My Location</span>
          </button>

          {/* Tracking Toggle */}
          <button
            onClick={toggleTracking}
            className={`flex-1 rounded-lg shadow-lg border p-3 flex items-center justify-center space-x-2 transition-colors ${
              tracking
                ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                : 'bg-green-500 text-white border-green-500 hover:bg-green-600'
            }`}
          >
            <Navigation className="w-5 h-5" />
            <span className="text-sm font-medium">
              {tracking ? 'Stop Tracking' : 'Start Tracking'}
            </span>
          </button>
        </div>
      </div>

      {/* Overlay to close menu when clicking outside */}
      {menuOpen && (
        <div
          className="absolute inset-0 z-10"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
